import { isCheckboxValueChecked, type FieldRecipientAccess } from "./template-fields"

export type FormRuleComparisonOperator =
  | "equals"
  | "not_equals"
  | "is_filled"
  | "is_empty"
  | "checked"
  | "unchecked"
  | "gt"
  | "gte"
  | "lt"
  | "lte"

export type FormRuleCondition =
  | {
      kind: "field"
      fieldName: string
      operator: FormRuleComparisonOperator
      value?: string
    }
  | {
      kind: "context"
      key: string
      operator: FormRuleComparisonOperator
      value?: string
    }
  | {
      kind: "all"
      conditions: FormRuleCondition[]
    }
  | {
      kind: "any"
      conditions: FormRuleCondition[]
    }
  | {
      kind: "not"
      condition: FormRuleCondition
    }

export type FormRuleEffect =
  | {
      type: "setVisibility"
      visible: boolean
      targetFieldName?: string
    }
  | {
      type: "setRequired"
      required: boolean
      targetFieldName?: string
    }
  | {
      type: "setRecipientAccess"
      value: FieldRecipientAccess
      targetFieldName?: string
    }

export type FormRule = {
  id?: string
  when: FormRuleCondition
  effects: FormRuleEffect[]
}

export type FormRuleContext = Record<string, unknown>

type LegacyConditionalOn = {
  fieldName: string
  operator: "equals" | "not_equals" | "is_filled" | "is_empty" | "checked" | "unchecked"
  value?: string
}

type RuleField = {
  fieldName: string
  rules?: FormRule[]
  conditionalOn?: LegacyConditionalOn
}

type ComputeFieldRuleStateOptions<TField extends RuleField> = {
  context?: FormRuleContext
  getFieldId?: (field: TField) => string
  getFieldValue: (field: TField) => unknown
}

export type ComputedFieldRuleState = {
  hiddenFieldIds: Set<string>
  hiddenFieldNames: Set<string>
  requiredOverridesByFieldId: Map<string, boolean>
  requiredOverridesByFieldName: Map<string, boolean>
  recipientAccessOverridesByFieldId: Map<string, FieldRecipientAccess>
  recipientAccessOverridesByFieldName: Map<string, FieldRecipientAccess>
}

export function evaluateFormRuleCondition(
  condition: FormRuleCondition,
  fieldValues: Record<string, unknown>,
  context: FormRuleContext = {},
): boolean {
  switch (condition.kind) {
    case "field":
      return evaluateComparison(
        fieldValues[condition.fieldName],
        condition.operator,
        condition.value,
      )
    case "context":
      return evaluateComparison(context[condition.key], condition.operator, condition.value)
    case "all":
      return condition.conditions.every((child) =>
        evaluateFormRuleCondition(child, fieldValues, context),
      )
    case "any":
      return condition.conditions.some((child) =>
        evaluateFormRuleCondition(child, fieldValues, context),
      )
    case "not":
      return !evaluateFormRuleCondition(condition.condition, fieldValues, context)
  }
}

export function computeFieldRuleState<TField extends RuleField>(
  fields: TField[],
  options: ComputeFieldRuleStateOptions<TField>,
): ComputedFieldRuleState {
  const getFieldId = options.getFieldId ?? ((field: TField) => field.fieldName)
  const fieldValuesByName = Object.fromEntries(
    fields.map((field) => [field.fieldName, options.getFieldValue(field)]),
  )

  let hiddenFieldNames = new Set<string>()
  let requiredOverridesByFieldName = new Map<string, boolean>()
  let recipientAccessOverridesByFieldName = new Map<string, FieldRecipientAccess>()
  const maxPasses = 10

  for (let pass = 0; pass < maxPasses; pass += 1) {
    const nextHiddenFieldNames = new Set<string>()
    const nextRequiredOverridesByFieldName = new Map<string, boolean>()
    const nextRecipientAccessOverridesByFieldName = new Map<string, FieldRecipientAccess>()

    for (const field of fields) {
      if (field.conditionalOn) {
        const controllingField = fields.find(
          (candidate) => candidate.fieldName === field.conditionalOn?.fieldName,
        )
        const controllingFieldName = field.conditionalOn.fieldName
        const conditionMet =
          !controllingField ||
          (!hiddenFieldNames.has(controllingFieldName) &&
            evaluateComparison(
              fieldValuesByName[controllingFieldName],
              field.conditionalOn.operator,
              field.conditionalOn.value,
            ))

        if (!conditionMet) {
          nextHiddenFieldNames.add(field.fieldName)
        }
      }

      for (const rule of field.rules ?? []) {
        if (!evaluateFormRuleCondition(rule.when, fieldValuesByName, options.context)) {
          continue
        }

        for (const effect of rule.effects) {
          const targetFieldName = effect.targetFieldName ?? field.fieldName
          switch (effect.type) {
            case "setVisibility":
              if (!effect.visible) {
                nextHiddenFieldNames.add(targetFieldName)
              } else {
                nextHiddenFieldNames.delete(targetFieldName)
              }
              break
            case "setRequired":
              nextRequiredOverridesByFieldName.set(targetFieldName, effect.required)
              break
            case "setRecipientAccess":
              nextRecipientAccessOverridesByFieldName.set(targetFieldName, effect.value)
              break
          }
        }
      }
    }

    if (
      setsEqual(hiddenFieldNames, nextHiddenFieldNames) &&
      mapsEqual(requiredOverridesByFieldName, nextRequiredOverridesByFieldName) &&
      mapsEqual(recipientAccessOverridesByFieldName, nextRecipientAccessOverridesByFieldName)
    ) {
      hiddenFieldNames = nextHiddenFieldNames
      requiredOverridesByFieldName = nextRequiredOverridesByFieldName
      recipientAccessOverridesByFieldName = nextRecipientAccessOverridesByFieldName
      break
    }

    hiddenFieldNames = nextHiddenFieldNames
    requiredOverridesByFieldName = nextRequiredOverridesByFieldName
    recipientAccessOverridesByFieldName = nextRecipientAccessOverridesByFieldName
  }

  const hiddenFieldIds = new Set<string>()
  const requiredOverridesByFieldId = new Map<string, boolean>()
  const recipientAccessOverridesByFieldId = new Map<string, FieldRecipientAccess>()

  for (const field of fields) {
    const fieldId = getFieldId(field)
    if (hiddenFieldNames.has(field.fieldName)) {
      hiddenFieldIds.add(fieldId)
    }

    const requiredOverride = requiredOverridesByFieldName.get(field.fieldName)
    if (requiredOverride !== undefined) {
      requiredOverridesByFieldId.set(fieldId, requiredOverride)
    }

    const recipientAccessOverride = recipientAccessOverridesByFieldName.get(field.fieldName)
    if (recipientAccessOverride !== undefined) {
      recipientAccessOverridesByFieldId.set(fieldId, recipientAccessOverride)
    }
  }

  return {
    hiddenFieldIds,
    hiddenFieldNames,
    requiredOverridesByFieldId,
    requiredOverridesByFieldName,
    recipientAccessOverridesByFieldId,
    recipientAccessOverridesByFieldName,
  }
}

function evaluateComparison(
  rawValue: unknown,
  operator: FormRuleComparisonOperator,
  comparisonValue?: string,
) {
  const normalizedValue =
    typeof rawValue === "boolean" || typeof rawValue === "number"
      ? rawValue
      : typeof rawValue === "string"
        ? rawValue
        : ""
  const normalizedComparison = comparisonValue ?? ""

  switch (operator) {
    case "equals":
      return String(normalizedValue) === normalizedComparison
    case "not_equals":
      return String(normalizedValue) !== normalizedComparison
    case "is_filled":
      return isFilledValue(rawValue)
    case "is_empty":
      return !isFilledValue(rawValue)
    case "checked":
      return isCheckboxValueChecked(rawValue)
    case "unchecked":
      return !isCheckboxValueChecked(rawValue)
    case "gt":
      return compareNumeric(rawValue, comparisonValue) > 0
    case "gte":
      return compareNumeric(rawValue, comparisonValue) >= 0
    case "lt":
      return compareNumeric(rawValue, comparisonValue) < 0
    case "lte":
      return compareNumeric(rawValue, comparisonValue) <= 0
  }
}

function compareNumeric(left: unknown, right: string | undefined) {
  const leftNumber = typeof left === "number" ? left : Number(left)
  const rightNumber = Number(right)
  if (!Number.isFinite(leftNumber) || !Number.isFinite(rightNumber)) {
    return Number.NaN
  }
  return leftNumber - rightNumber
}

function isFilledValue(value: unknown) {
  if (value === undefined || value === null) return false
  if (typeof value === "boolean") return value
  if (typeof value === "number") return true
  return typeof value === "string" ? value.trim().length > 0 : true
}

function setsEqual<T>(left: Set<T>, right: Set<T>) {
  return left.size === right.size && [...left].every((item) => right.has(item))
}

function mapsEqual<K, V>(left: Map<K, V>, right: Map<K, V>) {
  return (
    left.size === right.size &&
    [...left.entries()].every(([key, value]) => right.get(key) === value)
  )
}
